import request from '../utils/request';
import type { ListResponse } from '../types/common';
import type { 
  InventoryUnit, 
  CustomerInventoryItem, 
  InventoryListParams,
  InventoryFilterParams 
} from '../types/inventory';

// 库存配置类型定义
export interface InventoryConfig {
  id: number;
  project: number;
  project_name: string;
  project_code: string;
  project_type: string;
  project_type_display: string;
  sorting_groups: SortingGroup[];
  inventory_summary: {
    total_units: number;
    loose_units: number;
    box_units: number;
    pallet_units: number;
    abnormal_units?: number;
    non_product_units?: number;
    total_groups: number;
    loose_groups: number;
    box_groups: number;
    pallet_groups: number;
    abnormal_groups?: number;
    non_product_groups?: number;
    ungrouped_groups?: number;
  };
  sku_count: number;
  created_at: string;
  updated_at: string;
}

// 分拣组类型定义
export interface SortingGroup {
  id: number;
  name: string;
  group_type: 'LOOSE' | 'BOX' | 'PALLET';
  group_type_display: string;
  inventory_unit_count: number;
}

// 库存统计信息类型定义
export interface InventorySummary {
  total_units: number;
  loose_units: number;
  box_units: number;
  pallet_units: number;
  total_contents: number;
  loose_contents: number;
  box_contents: number;
  pallet_contents: number;
}

// 查询参数类型定义
export interface InventoryConfigListParams {
  page?: number;
  page_size?: number;
  search?: string;
  project_code?: string;
  project_name?: string;
  ordering?: string;
}

// 查询参数类型定义
export interface InventoryUnitListParams {
  page?: number;
  page_size?: number;
  project?: number;
  unit_type?: string;
  source_type?: string;
  sorting_group?: number;
  search?: string;
  ordering?: string;
}

// 获取库存配置列表
export const getInventoryConfigList = (params: InventoryConfigListParams) => {
  return request.get<ListResponse<InventoryConfig>>('/api/v1/inventory/inventory-configs/', { params });
};

// 获取库存配置详情
export const getInventoryConfigDetail = (id: number) => {
  return request.get<InventoryConfig>(`/api/v1/inventory/inventory-configs/${id}/`);
};

// 创建库存配置
export const createInventoryConfig = (data: Partial<InventoryConfig>) => {
  return request.post<InventoryConfig>('/api/v1/inventory/inventory-configs/', data);
};

// 更新库存配置
export const updateInventoryConfig = (id: number, data: Partial<InventoryConfig>) => {
  return request.put<InventoryConfig>(`/api/v1/inventory/inventory-configs/${id}/`, data);
};

// 删除库存配置
export const deleteInventoryConfig = (id: number) => {
  return request.delete(`/api/v1/inventory/inventory-configs/${id}/`);
};

/**
 * 获取库存单元列表
 */
export async function getInventoryUnits(params?: InventoryUnitListParams) {
  const queryParams = {
    ...params,
    sorting_group__project_inventory_config__project: params?.project,
  };
  delete queryParams.project;

  // 发送库存单元列表请求

  return request.get<ListResponse<InventoryUnit>>('/api/v1/inventory/inventory-units/', { params: queryParams });
}

/**
 * 获取客户列表 - 仅员工和管理员可用
 */
export async function getCustomersList() {
  return request.get('/api/inventory/inventory-units/get_customers/');
}

/**
 * 获取客户当前库存列表
 * 这个API会返回客户的所有库存单元，包含项目、分拣组、产品等信息
 */
export async function getCustomerCurrentInventory(params?: InventoryListParams) {
  return request.get<ListResponse<CustomerInventoryItem>>('/api/v1/inventory/inventory-units/', { 
    params: {
      ...params,
      // 确保只获取当前客户的库存数据
      customer_only: true
    }
  });
}

/**
 * 获取库存单元详情（包含完整的产品信息）
 */
export async function getInventoryUnitDetail(id: string | number, params?: Record<string, any>) {
  return request.get<any>(`/api/v1/inventory/inventory-units/${id}/`, { params });
}

/**
 * 批量创建：LIU 库存单元联动查询
 */
export async function lookupLiuUnits(params?: {
  warehouse?: string | number;
  warehouse_id?: string | number;
  warehouse_code?: string;
  project_id?: string | number;
  project_code?: string;
  system_barcode?: string;
  inventory_unit?: string;
  unit_tag?: string;
  inventory_unit_tag?: string;
}) {
  return request.get<any>('/api/v1/inventory/inventory-units/liu-lookup/', { params });
}

/**
 * 创建库存单元
 */
export async function createInventoryUnit(data: Partial<InventoryUnit>) {
  return request.post<InventoryUnit>('/api/v1/inventory/inventory-units/', data);
}

/**
 * 更新库存单元
 */
export async function updateInventoryUnit(id: number, data: Partial<InventoryUnit>) {
  return request.put<InventoryUnit>(`/api/v1/inventory/inventory-units/${id}/`, data);
}

/**
 * 删除库存单元
 */
export async function deleteInventoryUnit(id: number) {
  return request.delete(`/api/v1/inventory/inventory-units/${id}/`);
}

/**
 * 通过系统条码查询库存单元
 */
export async function getInventoryUnitByBarcode(barcode: string, params?: Record<string, any>) {
  return request.get<InventoryUnit>(`/api/v1/inventory/inventory-units/by_barcode/`, {
    params: { barcode, ...(params || {}) }
  });
}

/**
 * 通过产品信息搜索库存单元（精确匹配，忽略大小写）
 */
export async function searchInventoryUnitByProduct(params: { sorting_code?: string; custom_code?: string }) {
  return request.get<any>(`/api/v1/inventory/inventory-units/search_by_product/`, {
    params
  });
}

/**
 * 确认库存单元重量尺寸
 */
export async function confirmInventoryUnitWeightDimensions(
  id: number, 
  data: {
    actual_weight: number;
    actual_length: number;
    actual_width: number;
    actual_height: number;
    logistics_package?: boolean;
  }
) {
  return request.put<{
    detail: string;
    inventory_unit: InventoryUnit;
  }>(`/api/v1/inventory/inventory-units/${id}/confirm_weight_dimensions/`, data);
}

/**
 * 创建原箱/原托盘库存单元
 */
export async function createOriginalContainerUnit(data: {
  project_code: string;
  container_type: 'box' | 'pallet';
  actual_length?: number;
  actual_width?: number;
  actual_height?: number;
  actual_weight?: number;
  remarks?: string;
}) {
  return request.post<{
    detail: string;
    inventory_unit: InventoryUnit;
  }>('/api/v1/inventory/inventory-units/create_original_container_unit/', data);
}

/**
 * 创建配件/包材/标签等非上架产品库存单元
 */
export async function createAccessoriesInventoryUnit(data: {
  project_code: string;
  item_type: string;
  actual_length?: number;
  actual_width?: number;
  actual_height?: number;
  actual_weight?: number;
  remarks?: string;
}) {
  return request.post<{
    detail: string;
    inventory_unit: InventoryUnit;
  }>('/api/v1/inventory/inventory-units/create_accessories_inventory_unit/', data);
}

/**
 * 创建异常库存单元
 */
export async function createExceptionInventoryUnit(data: {
  project_code: string;
  actual_length?: number;
  actual_width?: number;
  actual_height?: number;
  remarks?: string;
}) {
  return request.post<{
    detail: string;
    inventory_unit: InventoryUnit;
  }>('/api/v1/inventory/inventory-units/create_exception_inventory_unit/', data);
}

/**
 * 创建库存单元内容
 */
export async function createInventoryContent(data: {
  inventory_unit: number;
  remarks?: string;
}) {
  return request.post<{
    id: number;
    inventory_unit: number;
    remarks?: string;
    created_at: string;
    updated_at: string;
  }>('/api/v1/inventory/inventory-contents/', data);
}

/**
 * 创建库存单元内容项
 */
export async function createInventoryContentItem(data: {
  content: number;
  product: number;
  quantity: number;
  remarks?: string;
}) {
  return request.post<{
    id: number;
    content: number;
    product: number;
    quantity: number;
    remarks?: string;
    created_at: string;
    updated_at: string;
  }>('/api/v1/inventory/inventory-content-items/', data);
}

/**
 * 创建带有分拣组的库存单元
 */
export async function createInventoryUnitWithSortingGroup(data: {
  unit_type: string;
  source_type: string;
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  weight_dimensions_confirmed: boolean;
  actual_weight?: number;
  actual_length?: number;
  actual_width?: number;
  actual_height?: number;
  sorting_group_name: string;
  project_id: number;
  remarks?: string;
}) {
  return request.post<{
    detail: string;
    inventory_unit: InventoryUnit;
  }>('/api/v1/inventory/inventory-units/create_with_sorting_group/', data);
} 

/**
 * 批量下载库存记录
 */
export const downloadInventoryRecords = async (params: {
  filters: any;
  selected_fields: string[];
  file_format?: 'csv';
}): Promise<void> => {
  let hideLoading: any = null;
  
  try {
    // 验证字段选择
    if (!params.selected_fields || params.selected_fields.length === 0) {
      throw new Error('请至少选择一个字段');
    }
    
    // 获取所有数据（忽略分页）
    const allData = await getAllInventoryData(params.filters);
    
    // 生成CSV内容
    const csvContent = generateInventoryCSVContent(allData, params.selected_fields);
    
    // 下载文件
    const filename = `库存数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    
    downloadCSVFile(csvContent, filename);
    
  } catch (error) {
    console.error('❌ 下载过程中出错:', error);
    throw error;
  } finally {
    if (hideLoading) {
      hideLoading();
    }
  }
};

/**
 * 获取所有库存数据（忽略分页）
 */
const getAllInventoryData = async (filters: any): Promise<any[]> => {
  let allData: any[] = [];
  let page = 1;
  let hasMore = true;
  
  while (hasMore) {
    const response = await getCustomerCurrentInventory({
      ...filters,
      page,
      page_size: 1000  // 使用较大的页面大小减少请求次数
    });
    
    allData = [...allData, ...response.data.results];
    
    if (response.data.next) {
      page++;
    } else {
      hasMore = false;
    }
  }
  
  return allData;
};

/**
 * 生成库存CSV内容
 */
const generateInventoryCSVContent = (allData: any[], selectedFields: string[]): string => {
  // 强制验证：必须有选择的字段
  if (!selectedFields || selectedFields.length === 0) {
    throw new Error('没有选择任何字段');
  }
  
  // 字段映射配置 - 对应新的字段分组
  const FIELD_MAPPING = {
    // 基本信息
    customer_name: { 
      label: '所属客户', 
      getValue: (record: any) => record.customer_name || '' 
    },
    system_barcode: { 
      label: '系统编码', 
      getValue: (record: any) => record.system_barcode || '' 
    },
    unit_type_display: { 
      label: '库存单元类型', 
      getValue: (record: any) => record.unit_type_display || '' 
    },
    project_name: { 
      label: '项目名称', 
      getValue: (record: any) => {
        if (record.project_info?.name) return record.project_info.name;
        if (record.project_name) return record.project_name;
        return '';
      }
    },
    project_code: { 
      label: '项目编码', 
      getValue: (record: any) => {
        if (record.project_info?.project_code) return record.project_info.project_code;
        if (record.project_code) return record.project_code;
        return '';
      }
    },
    sorting_group_name: { 
      label: '分拣组名称', 
      getValue: (record: any) => {
        if (record.sorting_group_info?.name) return record.sorting_group_info.name;
        if (record.sorting_group_name) return record.sorting_group_name;
        return '';
      }
    },
    sorting_group_code: { 
      label: '分拣组编码', 
      getValue: (record: any) => {
        if (record.sorting_group_info?.system_code) return record.sorting_group_info.system_code;
        if (record.sorting_group_code) return record.sorting_group_code;
        return '';
      }
    },
    source: { 
      label: '来源类型', 
      getValue: (record: any) => {
        const sourceMap = {
          'customer': '客户创建',
          'warehouse': '仓库操作创建',
          'other': '其他'
        };
        
        // 必须从record.source_type直接获取
        const sourceValue = record.source_type;
        
        return sourceMap[sourceValue as keyof typeof sourceMap] || sourceValue || '';
      }
    },
    on_hand_qty: {
      label: '总库存',
      getValue: (record: any) => {
        const value = record.on_hand_qty ?? record.warehouse_total_on_hand_quantity;
        return value ?? 0;
      }
    },
    reserved_qty: {
      label: '锁定库存',
      getValue: (record: any) => {
        const value = record.reserved_qty ?? record.warehouse_locked_quantity;
        return value ?? 0;
      }
    },
    available_qty: {
      label: '可用库存',
      getValue: (record: any) => {
        const value = record.available_qty ?? record.warehouse_available_quantity;
        return value ?? 0;
      }
    },
    warehouse_quantities: { 
      label: '可用库存分布', 
      getValue: (record: any) => {
        if (record.warehouse_quantities && Object.keys(record.warehouse_quantities).length > 0) {
          return Object.entries(record.warehouse_quantities)
            .map(([warehouse, qty]) => `${warehouse}×${qty}件`)
            .join(', ');
        }
        return '';
      }
    },

    // 所含产品信息
    product_system_code: { 
      label: '产品系统编码', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取产品系统编码
        if (record.content?.items?.length > 0) {
          if (record.content.items.length === 1) {
            // 单个产品：直接显示
            const firstItem = record.content.items[0];
            if (firstItem.system_code) {
              return firstItem.system_code;
            }
          } else {
            // 多个产品：汇总所有产品的系统编码
            const systemCodes = record.content.items
              .map((item: any) => item.system_code)
              .filter(Boolean);
            return systemCodes.join('; ');
          }
        }
        
        // 如果没有内容项，返回空字符串
        return '';
      }
    },
    product_name: { 
      label: '产品名称', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取产品名称
        if (record.content?.items?.length > 0) {
          if (record.content.items.length === 1) {
            // 单个产品：直接显示
            const firstItem = record.content.items[0];
            if (firstItem.product_name) {
              return firstItem.product_name;
            }
          } else {
            // 多个产品：汇总所有产品的名称
            const productNames = record.content.items
              .map((item: any) => item.product_name)
              .filter(Boolean);
            return productNames.join('; ');
          }
        }
        
        // 如果没有内容项，返回空字符串
        return '';
      }
    },
    product_custom_code: { 
      label: '产品客户自定义编码', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取产品客户自定义编码
        if (record.content?.items?.length > 0) {
          if (record.content.items.length === 1) {
            // 单个产品：直接显示
            const firstItem = record.content.items[0];
            if (firstItem.product_code) {
              return firstItem.product_code;
            }
          } else {
            // 多个产品：汇总所有产品的客户自定义编码
            const productCodes = record.content.items
              .map((item: any) => item.product_code)
              .filter(Boolean);
            return productCodes.join('; ');
          }
        }
        
        // 如果没有内容项，返回空字符串
        return '';
      }
    },
    product_sorting_barcode: { 
      label: '产品分拣条码', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取产品分拣条码
        if (record.content?.items?.length > 0) {
          if (record.content.items.length === 1) {
            // 单个产品：直接显示
            const firstItem = record.content.items[0];
            if (firstItem.sorting_codes?.length > 0) {
              return firstItem.sorting_codes.map((code: any) => code.code).join('; ');
            }
          } else {
            // 多个产品：汇总所有产品的分拣条码
            const sortingCodes = record.content.items
              .map((item: any) => {
                if (item.sorting_codes?.length > 0) {
                  return item.sorting_codes.map((code: any) => code.code).join(',');
                }
                return '';
              })
              .filter(Boolean);
            return sortingCodes.join('; ');
          }
        }
        
        // 如果没有内容项，返回空字符串
        return '';
      }
    },
    product_quantity: { 
      label: '所含产品数量', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取数量
        if (record.content?.items?.length > 0) {
          if (record.content.items.length === 1) {
            // 单个产品：直接返回数量
            const firstItem = record.content.items[0];
            const quantity = firstItem.quantity || 0;
            
            // 确保返回的是字符串格式，避免CSV处理问题
            return quantity.toString();
          } else {
            // 如果有多个产品，分别显示每个产品的数量
            return record.content.items
              .map((item: any) => {
                const productName = item.product_name || '未知产品';
                const quantity = item.quantity || 0;
                return `${productName} × ${quantity}件`;
              })
              .join('；');
          }
        }
        
        // 如果没有内容项，返回空字符串
        return '';
      }
    },
    product_remarks: { 
      label: '备注', 
      getValue: (record: any) => {
        // 从库存单元记录的remarks字段获取值
        if (record.remarks) {
          return record.remarks;
        }
        
        // 如果没有备注，返回空字符串
        return '';
      }
    },

    // 新增：产品种类数量字段
    product_count: { 
      label: '产品种类数量(种)', 
      getValue: (record: any) => {
        // 只从库存单元内容项获取产品种类数量
        if (record.content?.items?.length > 0) {
          return record.content.items.length;
        }
        
        // 如果没有内容项，返回0
        return 0;
      }
    },

    // 包装信息
    declared_actual_dimensions: { 
      label: '声明/实际尺寸(cm)', 
      getValue: (record: any) => {
        const declared = record.length && record.width && record.height ? 
          `${record.length}×${record.width}×${record.height}` : '-';
        const actual = record.actual_length && record.actual_width && record.actual_height ? 
          `${record.actual_length}×${record.actual_width}×${record.actual_height}` : '-';
        
        // 在数据前添加零宽空格，强制Excel识别为文本格式（最优雅）
        return `\u200B${declared}/${actual}`;
      }
    },
    declared_actual_weight: { 
      label: '声明/实际重量(g)', 
      getValue: (record: any) => {
        const declared = record.weight ? `${record.weight}` : '-';
        const actual = record.actual_weight ? `${record.actual_weight}` : '-';
        
        // 在数据前添加零宽空格，强制Excel识别为文本格式（最优雅）
        return `\u200B${declared}/${actual}`;
      }
    },
    unit_volume: { 
      label: '声明/实际单位体积(cm³)', 
      getValue: (record: any) => {
        const declared = record.length && record.width && record.height ? 
          (record.length * record.width * record.height).toFixed(0) : '-';
        const actual = record.actual_length && record.actual_width && record.actual_height ? 
          (record.actual_length * record.actual_width * record.actual_height).toFixed(0) : '-';
        
        // 在数据前添加零宽空格，强制Excel识别为文本格式（最优雅）
        return `\u200B${declared}/${actual}`;
      }
    },
    weight_dimensions_confirmed: { 
      label: '重量尺寸确认状态', 
      getValue: (record: any) => record.weight_dimensions_confirmed ? '已确认' : '未确认' 
    },
    logistics_package: { 
      label: '物流包装', 
      getValue: (record: any) => {
        const packageMap = {
          'self_contained': '自带物流包装',
          'no_package': '无物流包装',
          'not_applicable': '不适用'
        };
        return packageMap[record.logistics_package as keyof typeof packageMap] || record.logistics_package || '';
      }
    },

    // 业务信息
    recent_operation: { 
      label: '最近变动记录', 
      getValue: (record: any) => {
        if (record.recent_operation) {
          const { operation_type, change_direction, quantity_change, warehouse, operation_time } = record.recent_operation;
          const quantityDisplay = quantity_change > 0 ? `+${quantity_change}` : quantity_change.toString();
          
          // 格式化日期
          let dateDisplay = '';
          if (operation_time) {
            try {
              const date = new Date(operation_time);
              dateDisplay = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            } catch (error) {
              console.warn('日期格式化失败:', operation_time, error);
            }
          }
          
          // 构建返回字符串
          let result = `${operation_type} ${change_direction} ${quantityDisplay}`;
          if (warehouse) {
            result += ` (${warehouse})`;
          }
          if (dateDisplay) {
            result += ` [${dateDisplay}]`;
          }
          
          return result;
        }
        return '';
      }
    },
    calc_total_volume: { 
      label: '总库容体积(CBM)', 
      getValue: (record: any) => {
        const onHandQty = Number(record.on_hand_qty ?? record.warehouse_total_on_hand_quantity ?? 0);
        if (record.actual_length && record.actual_width && record.actual_height && onHandQty > 0) {
          const unitVolume = record.actual_length * record.actual_width * record.actual_height;
          const totalVolume = unitVolume * onHandQty;
          return (totalVolume / 1000000).toFixed(3);
        }
        return '';
      }
    },
    storage_fee_summary: { 
      label: '仓储费汇总', 
      getValue: (record: any) => {
        // 根据库存单元类型和数量计算预估仓储费
        if (record.total_quantity > 0) {
          let unitFee = 0;
          switch (record.unit_type) {
            case 'LIU': // 散货
              unitFee = 0.5; // 每件0.5元/月
              break;
            case 'BIU': // 箱
              unitFee = 2.0; // 每箱2元/月
              break;
            case 'PIU': // 托盘
              unitFee = 10.0; // 每托盘10元/月
              break;
            case 'AIU': // 异常
              unitFee = 1.0; // 每件1元/月
              break;
            case 'NIU': // 非产品
              unitFee = 0.3; // 每件0.3元/月
              break;
            default:
              unitFee = 0.5;
          }
          const monthlyFee = unitFee * record.total_quantity;
          return `预估月费: ¥${monthlyFee.toFixed(2)}`;
        }
        return '无库存';
      }
    },
    storage_age_summary: { 
      label: '库龄汇总', 
      getValue: (record: any) => {
        // 改为按“库龄体积分布”导出：0-30/31-60/61-90/90+
        const buckets = (record && (record.age_bucket_volumes || (record as any)?.age_bucket_volumes)) || null;
        if (!buckets || typeof buckets !== 'object') return '-';
        const ordered = ['0-30', '31-60', '61-90', '90+'];
        const parts: string[] = [];
        for (const k of ordered) {
          const v = (buckets as any)[k];
          if (typeof v === 'number' && !isNaN(v)) {
            parts.push(`${k}天 ${Number(v).toFixed(3)} m³`);
          }
        }
        return parts.length > 0 ? parts.join('；') : '-';
      }
    }
  } as const;

  // 字段类型定义
  type FieldKey = keyof typeof FIELD_MAPPING;
  
  // 使用传入的selected_fields，不再有默认值
  const fieldsToExport = selectedFields;
  
  // 生成表头
  const headers = fieldsToExport.map(fieldKey => {
    const field = FIELD_MAPPING[fieldKey as FieldKey];
    return field ? field.label : fieldKey;
  });
  
  // 生成数据行
  let csvContent = '\ufeff'; // UTF-8 BOM
  csvContent += headers.join(',') + '\n';
  
  allData.forEach((record: any) => {
    const row = fieldsToExport.map(fieldKey => {
      try {
        const field = FIELD_MAPPING[fieldKey as FieldKey];
        if (field) {
          return field.getValue(record);
        } else {
          console.warn(`⚠️ 未找到字段映射: ${fieldKey}`);
          return '';
        }
      } catch (error) {
        console.error(`❌ 处理字段 ${fieldKey} 时出错:`, error);
        return `[错误: ${fieldKey}]`;
      }
    });
    
    // 处理特殊字符（强制要求）
    const processedRow = row.map(cell => {
      const cellStr = cell === null || cell === undefined ? '' : String(cell);
      
      if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('\r') || cellStr.includes('"')) {
        const escapedCell = cellStr.replace(/"/g, '""');
        return `"${escapedCell}"`;
      }
      
      return cellStr;
    });
    
    csvContent += processedRow.join(',') + '\n';
  });
  
  return csvContent;
};

/**
 * 下载CSV文件
 */
const downloadCSVFile = (csvContent: string, filename: string): void => {
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } else {
      console.warn('⚠️ 浏览器不支持download属性');
    }
  } catch (error) {
    console.error('❌ 下载过程中出错:', error);
  }
}; 